import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Shield, 
  Clock, 
  Lock, 
  Users,
  Save,
  Loader2,
  ArrowLeft,
  AlertTriangle,
  Info
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Alert, AlertDescription } from '../components/ui/alert';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AVAILABLE_ROLES = [
  { id: 'system_admin', label: 'System Administrator' },
  { id: 'executive', label: 'Executive' },
  { id: 'operations_manager', label: 'Operations Manager' },
  { id: 'program_director', label: 'Program Director' },
  { id: 'case_manager_supervisor', label: 'Case Manager Supervisor' }
];

const SecuritySettingsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    auto_logout_enabled: true,
    auto_logout_time: '00:00',
    exempt_roles: ['system_admin', 'executive'],
    session_timeout_minutes: 480,
    require_password_change_days: 90,
    lockout_threshold: 5,
    lockout_duration_minutes: 30
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/security-settings`);
      setSettings(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error('Failed to fetch security settings:', error);
      toast.error('Failed to load security settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/security-settings`, settings);
      toast.success('Security settings saved successfully');
    } catch (error) {
      console.error('Failed to save security settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const toggleExemptRole = (roleId) => {
    setSettings(prev => {
      const current = prev.exempt_roles || [];
      if (current.includes(roleId)) {
        return { ...prev, exempt_roles: current.filter(r => r !== roleId) };
      } else {
        return { ...prev, exempt_roles: [...current, roleId] };
      }
    });
  };

  if (user?.role !== 'system_admin') {
    return (
      <div className="flex items-center justify-center h-96">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <Shield className="w-12 h-12 mx-auto mb-4 text-destructive" />
            <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
            <p className="text-muted-foreground">
              Only System Administrators can access security settings.
            </p>
            <Button className="mt-4" onClick={() => navigate('/settings')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Settings
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="security-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/settings')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Shield className="w-6 h-6 text-primary" />
              Security Settings
            </h1>
            <p className="text-muted-foreground">
              Configure security policies and automatic session management
            </p>
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="save-security-settings">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Save Changes
        </Button>
      </div>

      {/* Auto Logout Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-amber-500" />
            Automatic Daily Logout
          </CardTitle>
          <CardDescription>
            Automatically sign users out at a specific time each day to enhance security
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <Alert>
            <Info className="w-4 h-4" />
            <AlertDescription>
              When enabled, users will be automatically logged out at the specified time based on their assigned timezone. 
              This only affects interactive user sessions and does not interrupt background services, scheduled jobs, or automated workflows.
            </AlertDescription>
          </Alert>

          <div className="flex items-center justify-between p-4 rounded-lg border">
            <div className="space-y-1">
              <Label className="text-base font-medium">Enable Automatic Logout</Label>
              <p className="text-sm text-muted-foreground">
                Users will be signed out automatically at the configured time
              </p>
            </div>
            <Switch
              checked={settings.auto_logout_enabled}
              onCheckedChange={(checked) => setSettings(prev => ({ ...prev, auto_logout_enabled: checked }))}
              data-testid="auto-logout-toggle"
            />
          </div>

          {settings.auto_logout_enabled && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="logout_time">Logout Time (24-hour format)</Label>
                  <Input
                    id="logout_time"
                    type="time"
                    value={settings.auto_logout_time}
                    onChange={(e) => setSettings(prev => ({ ...prev, auto_logout_time: e.target.value }))}
                    data-testid="logout-time-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Time is evaluated based on each user's assigned timezone
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <Label className="text-base font-medium">Role Exemptions</Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    Select roles that should be exempt from automatic logout
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {AVAILABLE_ROLES.map((role) => (
                    <div
                      key={role.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        settings.exempt_roles?.includes(role.id) 
                          ? 'border-primary bg-primary/5' 
                          : 'hover:bg-muted/50'
                      }`}
                      onClick={() => toggleExemptRole(role.id)}
                    >
                      <Checkbox
                        checked={settings.exempt_roles?.includes(role.id)}
                        onCheckedChange={() => toggleExemptRole(role.id)}
                      />
                      <div>
                        <p className="font-medium text-sm">{role.label}</p>
                      </div>
                    </div>
                  ))}
                </div>
                {settings.exempt_roles?.length === 0 && (
                  <Alert variant="destructive">
                    <AlertTriangle className="w-4 h-4" />
                    <AlertDescription>
                      Warning: No roles are exempt. All users, including administrators, will be logged out automatically.
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Session Security */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-blue-500" />
            Session Security
          </CardTitle>
          <CardDescription>
            Configure session timeout and lockout policies
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="session_timeout">Session Timeout (minutes)</Label>
              <Input
                id="session_timeout"
                type="number"
                min="30"
                max="1440"
                value={settings.session_timeout_minutes}
                onChange={(e) => setSettings(prev => ({ ...prev, session_timeout_minutes: parseInt(e.target.value) || 480 }))}
              />
              <p className="text-xs text-muted-foreground">
                Time of inactivity before automatic logout (30-1440 minutes)
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="lockout_threshold">Failed Login Attempts Before Lockout</Label>
              <Input
                id="lockout_threshold"
                type="number"
                min="3"
                max="10"
                value={settings.lockout_threshold}
                onChange={(e) => setSettings(prev => ({ ...prev, lockout_threshold: parseInt(e.target.value) || 5 }))}
              />
              <p className="text-xs text-muted-foreground">
                Number of failed login attempts before account lockout
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="lockout_duration">Lockout Duration (minutes)</Label>
              <Input
                id="lockout_duration"
                type="number"
                min="5"
                max="1440"
                value={settings.lockout_duration_minutes}
                onChange={(e) => setSettings(prev => ({ ...prev, lockout_duration_minutes: parseInt(e.target.value) || 30 }))}
              />
              <p className="text-xs text-muted-foreground">
                How long an account remains locked after too many failed attempts
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password_change">Password Change Requirement (days)</Label>
              <Input
                id="password_change"
                type="number"
                min="0"
                max="365"
                value={settings.require_password_change_days}
                onChange={(e) => setSettings(prev => ({ ...prev, require_password_change_days: parseInt(e.target.value) || 90 }))}
              />
              <p className="text-xs text-muted-foreground">
                Days before users must change their password (0 to disable)
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Current Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-green-500" />
            Current Configuration Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 rounded-lg bg-muted/50">
              <p className="text-2xl font-bold text-primary">
                {settings.auto_logout_enabled ? settings.auto_logout_time : 'Disabled'}
              </p>
              <p className="text-xs text-muted-foreground">Auto Logout Time</p>
            </div>
            <div className="text-center p-4 rounded-lg bg-muted/50">
              <p className="text-2xl font-bold text-primary">
                {settings.exempt_roles?.length || 0}
              </p>
              <p className="text-xs text-muted-foreground">Exempt Roles</p>
            </div>
            <div className="text-center p-4 rounded-lg bg-muted/50">
              <p className="text-2xl font-bold text-primary">
                {Math.floor(settings.session_timeout_minutes / 60)}h {settings.session_timeout_minutes % 60}m
              </p>
              <p className="text-xs text-muted-foreground">Session Timeout</p>
            </div>
            <div className="text-center p-4 rounded-lg bg-muted/50">
              <p className="text-2xl font-bold text-primary">
                {settings.lockout_threshold}
              </p>
              <p className="text-xs text-muted-foreground">Lockout Threshold</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SecuritySettingsPage;
